import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  type Column,
  type PaginationState,
  type SortingState,
  createColumnHelper,
  rowPaginationFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import { Sort } from 'iconoir-react';
import type { Schema } from 'effect';
import type { AdminUserRow } from '@listening/api-contract/admin-endpoints';
import { callApiPromise } from '@/runtimes/get-runtime';
import { useImpersonate } from '@/components/web-fe/admin/use-impersonate';
import { Button } from '@listening/frontend/components/radix/button';
import { Input } from '@listening/frontend/components/radix/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@listening/frontend/components/radix/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@listening/frontend/components/radix/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@listening/frontend/components/radix/dialog';

export const Route = createFileRoute('/app/_authenticated/admin/')({
  component: AdminUsersPage,
});

type AdminUser = Schema.Schema.Type<typeof AdminUserRow>;

// The sorted and paginated row model slots stay unregistered: the API owns
// both, so `manualSorting`/`manualPagination` short-circuit to the core model.
const features = tableFeatures({
  rowSortingFeature,
  rowPaginationFeature,
});

const EMPTY_USERS: ReadonlyArray<AdminUser> = [];

// =============================================================================
// Column Definitions
// =============================================================================

function SortableHeader<TValue>({
  column,
  title,
}: {
  column: Column<typeof features, AdminUser, TValue>;
  title: string;
}) {
  return (
    <Button
      variant='ghost'
      size='sm'
      className='-ml-3 h-8'
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {title}
      <Sort className='ml-2 size-4' />
    </Button>
  );
}

const columnHelper = createColumnHelper<typeof features, AdminUser>();

const columns = columnHelper.columns([
  columnHelper.accessor('name', {
    header: ({ column }) => <SortableHeader column={column} title='Name' />,
    cell: ({ row }) => (
      <Link
        to='/app/admin/users/$userId'
        params={{ userId: row.original.id }}
        className='font-medium text-blue-600 hover:underline dark:text-blue-400'
      >
        {row.original.name || 'No name'}
      </Link>
    ),
  }),
  columnHelper.accessor('email', {
    header: ({ column }) => <SortableHeader column={column} title='Email' />,
  }),
  columnHelper.accessor('role', {
    header: ({ column }) => <SortableHeader column={column} title='Role' />,
    cell: ({ row }) => {
      const role = row.original.role;
      return (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
            role === 'admin'
              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
              : 'bg-gray-100 text-gray-600 dark:bg-navy-260 dark:text-steel-180'
          }`}
        >
          {role ?? 'user'}
        </span>
      );
    },
  }),
  columnHelper.display({
    id: 'banned',
    header: 'Banned',
    cell: ({ row }) =>
      row.original.banned ? (
        <span className='inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200'>
          Banned
        </span>
      ) : (
        <span className='inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200'>
          Active
        </span>
      ),
  }),
  columnHelper.accessor('subscriptionStatus', {
    header: ({ column }) => (
      <SortableHeader column={column} title='Subscription' />
    ),
    cell: ({ row }) => {
      const status = row.original.subscriptionStatus;
      const platform = row.original.subscriptionPlatform;
      if (!status) return <span className='text-gray-400'>None</span>;
      const color =
        status === 'active'
          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
          : status === 'trialing'
            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      return (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
        >
          {status}
          {platform ? ` (${platform})` : ''}
        </span>
      );
    },
  }),
  columnHelper.accessor('createdAt', {
    header: ({ column }) => <SortableHeader column={column} title='Created' />,
    cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
  }),
  columnHelper.display({
    id: 'lastSignedInAt',
    header: 'Last Signed In',
    cell: ({ row }) =>
      row.original.lastSignedInAt ? (
        new Date(row.original.lastSignedInAt).toLocaleDateString()
      ) : (
        <span className='text-gray-400'>Never</span>
      ),
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row }) => <RowActions user={row.original} />,
  }),
]);

// =============================================================================
// Row Actions
// =============================================================================

function RowActions({ user }: { user: AdminUser }) {
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const impersonateMutation = useImpersonate();

  return (
    <>
      <Button
        variant='ghost'
        size='sm'
        onClick={() => setImpersonateOpen(true)}
      >
        Impersonate
      </Button>

      <Dialog open={impersonateOpen} onOpenChange={setImpersonateOpen}>
        <DialogContent includeCloseButton>
          <DialogHeader>
            <DialogTitle>Confirm Impersonation</DialogTitle>
            <p className='mt-1 text-sm text-gray-500 dark:text-steel-180'>
              You are about to impersonate <strong>{user.email}</strong>. You
              will be redirected and act as this user.
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setImpersonateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => impersonateMutation.mutate(user.id)}
              disabled={impersonateMutation.isPending}
            >
              {impersonateMutation.isPending
                ? 'Impersonating...'
                : 'Impersonate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// =============================================================================
// Main Page
// =============================================================================

function AdminUsersPage() {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ]);
  const initialPagination: PaginationState = { pageIndex: 0, pageSize: 20 };
  const [pagination, setPagination] = useState(initialPagination);
  const [globalFilter, setGlobalFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [bannedFilter, setBannedFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');

  const sortField = (sorting[0]?.id ?? 'createdAt') as
    | 'name'
    | 'email'
    | 'createdAt'
    | 'role'
    | 'subscriptionStatus';
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';

  const usersQuery = useQuery({
    queryKey: [
      'admin',
      'users',
      pagination.pageIndex,
      pagination.pageSize,
      sortField,
      sortOrder,
      globalFilter,
      roleFilter,
      bannedFilter,
      subFilter,
    ],
    queryFn: () =>
      callApiPromise((api) =>
        api.Admin.ListUsers({
          query: {
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            sortField,
            sortOrder,
            ...(globalFilter ? { search: globalFilter } : {}),
            ...(roleFilter !== 'all'
              ? { role: roleFilter as 'admin' | 'user' }
              : {}),
            ...(bannedFilter !== 'all'
              ? { banned: bannedFilter as 'true' | 'false' }
              : {}),
            ...(subFilter !== 'all'
              ? {
                  subscriptionStatus: subFilter as
                    | 'active'
                    | 'trialing'
                    | 'canceled'
                    | 'none',
                }
              : {}),
          },
        }),
      ),
  });

  const data = usersQuery.data?.users ?? EMPTY_USERS;
  const total = usersQuery.data?.total ?? 0;

  const table = useTable({
    features,
    data,
    columns,
    manualSorting: true,
    manualPagination: true,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    pageCount: Math.ceil(total / pagination.pageSize),
    rowCount: total,
    state: {
      sorting,
      pagination,
    },
  });

  const handleSearch = () => {
    setGlobalFilter(searchInput.trim());
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <div>
      <h2 className='mb-6 text-2xl font-bold text-gray-900 dark:text-white'>
        User Management
      </h2>

      {/* Filters */}
      <div className='mb-4 flex flex-wrap items-end gap-3'>
        <div className='flex-1'>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-steel-180'>
            Search
          </label>
          <div className='flex gap-2'>
            <Input
              placeholder='Search by name or email...'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              className='dark:bg-navy-860'
            />
            <Button onClick={handleSearch} disabled={usersQuery.isFetching}>
              Search
            </Button>
          </div>
        </div>
        <div className='w-32'>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-steel-180'>
            Role
          </label>
          <Select
            value={roleFilter}
            onValueChange={(v) => {
              setRoleFilter(v);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          >
            <SelectTrigger className='dark:bg-navy-860'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All</SelectItem>
              <SelectItem value='admin'>Admin</SelectItem>
              <SelectItem value='user'>User</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className='w-32'>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-steel-180'>
            Banned
          </label>
          <Select
            value={bannedFilter}
            onValueChange={(v) => {
              setBannedFilter(v);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          >
            <SelectTrigger className='dark:bg-navy-860'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All</SelectItem>
              <SelectItem value='false'>Active</SelectItem>
              <SelectItem value='true'>Banned</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className='w-36'>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-steel-180'>
            Subscription
          </label>
          <Select
            value={subFilter}
            onValueChange={(v) => {
              setSubFilter(v);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          >
            <SelectTrigger className='dark:bg-navy-860'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All</SelectItem>
              <SelectItem value='active'>Active</SelectItem>
              <SelectItem value='trialing'>Trialing</SelectItem>
              <SelectItem value='canceled'>Canceled</SelectItem>
              <SelectItem value='none'>None</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className='rounded-lg border border-gray-200 bg-white dark:border-navy-260 dark:bg-navy-760'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : (
                      <table.FlexRender header={header} />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {usersQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getAllCells().map((cell) => (
                    <TableCell key={cell.id}>
                      <table.FlexRender cell={cell} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className='flex items-center justify-between py-4'>
        <div className='text-sm text-gray-500 dark:text-steel-180'>
          {total} total user{total !== 1 ? 's' : ''}
        </div>
        <div className='flex items-center gap-2'>
          <Select
            value={String(pagination.pageSize)}
            onValueChange={(v) =>
              setPagination({ pageIndex: 0, pageSize: Number(v) })
            }
          >
            <SelectTrigger className='h-8 w-[70px] dark:bg-navy-860'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className='text-sm text-gray-600 dark:text-steel-180'>
            Page {pagination.pageIndex + 1} of{' '}
            {Math.max(1, Math.ceil(total / pagination.pageSize))}
          </span>
          <Button
            variant='outline'
            size='sm'
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
